import { useCallback } from 'react';
import { Button, cn, Flex } from '@kognitos/lattice';
import type { IParsedFollowUpSuggestion } from '@/shared/utils/chat-utils';

export interface IFollowUpSuggestionsCardProps {
  /** Follow-up suggestions parsed from the agent message */
  suggestions: IParsedFollowUpSuggestion[];
  /** Sends the suggestion's message to the agent. When omitted, chips render disabled. */
  onSend?: (message: string) => void;
  /** Whether a message is currently being sent (disables the chips) */
  isSubmitting?: boolean;
  className?: string;
}

/**
 * Renders agent follow-up suggestions as a horizontal group of clickable pills
 * (the design-system "Button Group" widget). Each pill shows its `label` and
 * sends its `message` to the agent on click. Chips render disabled when no
 * `onSend` handler is provided (e.g. resolved/historical views) or while a
 * message is being sent.
 */
export function FollowUpSuggestionsCard({
  suggestions,
  onSend,
  isSubmitting = false,
  className,
}: IFollowUpSuggestionsCardProps) {
  const handleClick = useCallback(
    (message: string) => {
      if (isSubmitting || !onSend) return;
      onSend(message);
    },
    [isSubmitting, onSend],
  );

  if (suggestions.length === 0) {
    return null;
  }

  const disabled = isSubmitting || !onSend;

  return (
    <Flex
      direction="row"
      wrap="wrap"
      gap={2}
      className={cn('w-full', className)}
      data-testid="astral-follow-up-card"
    >
      {suggestions.map(suggestion => (
        <Button
          key={`${suggestion.label}|${suggestion.message}`}
          variant="outline"
          disabled={disabled}
          onClick={() => handleClick(suggestion.message)}
          className="h-auto min-w-0 max-w-full whitespace-normal py-2 text-left"
          data-testid="astral-follow-up-suggestion"
        >
          <span className="w-full min-w-0 break-words">{suggestion.label}</span>
        </Button>
      ))}
    </Flex>
  );
}

export default FollowUpSuggestionsCard;
